import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { trackPageView } from "./lib/analytics";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import Partners from "./pages/Partners";
import ImportClients from "./pages/ImportClients";
import Prospects from "./pages/Prospects";
import AirportServices from "./pages/AirportServices";
import ImportAirportServices from "./pages/ImportAirportServices";
import Transfers from "./pages/Transfers";
import ImportTransfers from "./pages/ImportTransfers";
import Hotels from "./pages/Hotels";
import ImportHotels from "./pages/ImportHotels";
import Tours from "./pages/Tours";
import ImportTours from "./pages/ImportTours";
import Attractions from "./pages/Attractions";
import ImportAttractions from "./pages/ImportAttractions";
import AdditionalRequestServices from "./pages/AdditionalRequestServices";
import Proposals from "./pages/Proposals";
import LegacyProposals from "./pages/LegacyProposals";
import ProposalBuilder from "./pages/ProposalBuilder";
import ProposalHistory from "./pages/ProposalHistory";
import ProposalHistoryPublicPreview from "./pages/ProposalHistoryPublicPreview";
import Suppliers from "./pages/Suppliers";
import PublicProposal from "./pages/PublicProposal";
import BookingPackage from "./pages/BookingPackage";
import BookingPackages from "./pages/BookingPackages";
import PublicBookingPackage from "./pages/PublicBookingPackage";
import CreateNewProposal from "./pages/CreateNewProposal";
import WeeklySalesReport from "./pages/WeeklySalesReport";
import CreateUser from "./pages/CreateUser";
import ImportProposals from "./pages/ImportProposals";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="highclass-theme">
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnalyticsTracker />
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Auth />} />
              <Route path="/proposal/:id" element={<PublicProposal />} />
              <Route path="/proposal/share/:token" element={<PublicProposal />} />
              <Route path="/booking/:id" element={<PublicBookingPackage />} />

              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/partners" element={<ProtectedRoute><Navigate to="/partners/agency" replace /></ProtectedRoute>} />
              <Route path="/partners/company" element={<ProtectedRoute><Navigate to="/partners/agency" replace /></ProtectedRoute>} />
              <Route path="/partners/final-clients" element={<ProtectedRoute><Navigate to="/partners/final-client" replace /></ProtectedRoute>} />
              <Route path="/partners/agency" element={<ProtectedRoute><Partners /></ProtectedRoute>} />
              <Route path="/partners/operator" element={<ProtectedRoute><Partners /></ProtectedRoute>} />
              <Route path="/partners/final-client" element={<ProtectedRoute><Partners /></ProtectedRoute>} />
              <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
              <Route path="/clients/import" element={<ProtectedRoute><ImportClients /></ProtectedRoute>} />
              <Route path="/prospects" element={<ProtectedRoute><Prospects /></ProtectedRoute>} />
              <Route path="/prospects/import" element={<ProtectedRoute><Navigate to="/prospects?import=1" replace /></ProtectedRoute>} />
              <Route path="/airport-services" element={<ProtectedRoute><AirportServices /></ProtectedRoute>} />
              <Route path="/airport-services/import" element={<ProtectedRoute><ImportAirportServices /></ProtectedRoute>} />
              <Route path="/transfers" element={<ProtectedRoute><Transfers /></ProtectedRoute>} />
              <Route path="/transfers/import" element={<ProtectedRoute><ImportTransfers /></ProtectedRoute>} />
              <Route path="/hotels" element={<ProtectedRoute><Hotels /></ProtectedRoute>} />
              <Route path="/import-hotels" element={<ProtectedRoute><ImportHotels /></ProtectedRoute>} />
              <Route path="/tours" element={<ProtectedRoute><Tours /></ProtectedRoute>} />
              <Route path="/import-tours" element={<ProtectedRoute><ImportTours /></ProtectedRoute>} />
              <Route path="/attractions" element={<ProtectedRoute><Attractions /></ProtectedRoute>} />
              <Route path="/import-attractions" element={<ProtectedRoute><ImportAttractions /></ProtectedRoute>} />
              <Route path="/additional-request-services" element={<ProtectedRoute><AdditionalRequestServices /></ProtectedRoute>} />
              <Route path="/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
              <Route path="/proposals" element={<ProtectedRoute><Proposals /></ProtectedRoute>} />
              <Route path="/proposals/:id/history" element={<ProtectedRoute><ProposalHistory /></ProtectedRoute>} />
              <Route path="/proposals/:id/history/:revisionId/public-preview" element={<ProtectedRoute><ProposalHistoryPublicPreview /></ProtectedRoute>} />
              <Route path="/proposals-legacy" element={<ProtectedRoute><LegacyProposals /></ProtectedRoute>} />
              <Route path="/proposals-legacy/import" element={<ProtectedRoute><ImportProposals /></ProtectedRoute>} />
              <Route path="/proposals/new" element={<ProtectedRoute><ProposalBuilder /></ProtectedRoute>} />
              <Route path="/proposals/:id" element={<ProtectedRoute><ProposalBuilder /></ProtectedRoute>} />
              <Route path="/proposals/import" element={<ProtectedRoute><ImportProposals /></ProtectedRoute>} />
              <Route path="/create-new-proposal" element={<ProtectedRoute><CreateNewProposal /></ProtectedRoute>} />
              <Route path="/new-lead" element={<ProtectedRoute><Navigate to="/create-new-proposal" replace /></ProtectedRoute>} />
              <Route path="/create-user" element={<ProtectedRoute><CreateUser /></ProtectedRoute>} />
              <Route path="/booking-packages" element={<ProtectedRoute><BookingPackages /></ProtectedRoute>} />
              <Route path="/reports/weekly-sales" element={<ProtectedRoute><WeeklySalesReport /></ProtectedRoute>} />
              <Route path="/booking-package/:id" element={<ProtectedRoute><BookingPackage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
